import { Grid, Typography, Button, Box, Skeleton } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import React, { useEffect, useState } from "react";
import useProjects from "../../../hooks/useProjects";

export default function CertificateUploadSection({
  projectDetails,
  certTemplateFile,
  certPreviewUrl,
  setCertTemplateFile,
  setCertPreviewUrl,
  setIsCertModalOpen,
  projectId,
  setCertBg,
}) {
  const {
    uploadCertificateBg,
    certificateBg,
    uploadingCertificateBg,
    isCertTemplateSaved,
    removeCertificateBg,
    removingCertificateBg,
    isCertTemplateRemoved,
    isFetchingProjectDetails,
  } = useProjects();

  const [isEditing, setIsEditing] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [isCertSaved, setIsCertSaved] = useState(false);
  const [isCertRemoved, setIsCertRemoved] = useState(false);

  // ===== Handle save success =====
  useEffect(() => {
    if (isCertTemplateSaved && certificateBg) {
      setCertBg(certificateBg);
      setLocalPreview(certificateBg);
      setIsCertSaved(true);
      setIsCertRemoved(false);
      setIsEditing(false);
    }
  }, [isCertTemplateSaved, certificateBg]);

  // ===== Handle remove success =====
  useEffect(() => {
    if (isCertTemplateRemoved) {
      setLocalPreview(null);
      setIsCertSaved(false);
      setIsCertRemoved(true);
      setIsEditing(false);
    }
  }, [isCertTemplateRemoved]);

  // ===== Handlers =====
  const handleCertUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertTemplateFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCertPreviewUrl(reader.result);
        setLocalPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCertSave = async () => {
    if (!certTemplateFile) return;
    const formData = new FormData();
    formData.append("cert_bg", certTemplateFile);
    formData.append("project_id", projectId);
    await uploadCertificateBg(formData);
    setCertTemplateFile(null);
    setCertPreviewUrl("");
  };

  const handleCertRemove = async () => {
    const formData = new FormData();
    formData.append("project_id", projectId);
    await removeCertificateBg(formData);
  };

  const handleCertCancel = () => {
    setIsEditing(false);
    setLocalPreview(projectDetails?.cert_bg_url || certificateBg || null);
    setCertTemplateFile(null);
    setCertPreviewUrl("");
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setLocalPreview(null);
  };

  const hasExistingCert =
    (projectDetails?.cert_bg_url || certificateBg || localPreview) &&
    !isCertRemoved &&
    !isEditing;

  const isEmptyCert =
    !projectDetails?.cert_bg_url &&
    !certificateBg &&
    !localPreview &&
    !isCertSaved;

  // ===== Render =====
  return (
    <Grid container sx={{ ml: 2, mt: "16px" }}>
      <Grid
        container
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
        sx={{ alignSelf: "center", display: "flex", mt: 1 }}
      >
        <Grid
          item
          xs={6}
          sm={6}
          md={4}
          lg={4}
          xl={4}
          sx={{ alignSelf: "center" }}
        >
          <Typography
            sx={{ fontWeight: "bold", alignSelf: "center", fontSize: 14 }}
          >
            Upload Certificate Template
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          {/* ===== Skeleton while reloading ===== */}
          {isFetchingProjectDetails ? (
            <Grid container spacing={2} alignItems="center">
              <Skeleton
                variant="rectangular"
                width={70}
                height={70}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton variant="text" width={60} height={30} />
              <Skeleton variant="text" width={60} height={30} />
              <Skeleton variant="text" width={60} height={30} />
            </Grid>
          ) : hasExistingCert ? (
            // ===== Saved / View Mode =====
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Box
                  component="img"
                  src={
                    certificateBg ||
                    projectDetails?.cert_bg_url ||
                    localPreview
                  }
                  alt="Certificate preview"
                  sx={{
                    width: 70,
                    height: 70,
                    objectFit: "contain",
                    borderRadius: 1,
                    border: "1px solid #c4c4c4",
                    padding: 0.2,
                  }}
                />
              </Grid>

              <Grid item>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsCertModalOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  View
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditClick}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Edit
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleCertRemove}
                  disabled={removingCertificateBg}
                  startIcon={
                    removingCertificateBg && (
                      <ThreeDots
                        height="20"
                        width="20"
                        radius="9"
                        color="#fff"
                      />
                    )
                  }
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  {removingCertificateBg ? "Removing" : "Remove"}
                </Button>
              </Grid>
            </Grid>
          ) : (
            // ===== Edit / Browse Mode =====
            <Grid container spacing={2} alignItems="center">
              {!localPreview ? (
                <>
                  <Grid item>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      Browse
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleCertUpload}
                      />
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="contained"
                      size="small"
                      disabled
                      sx={{
                        textTransform: "none",
                        fontWeight: 500,
                        opacity: 0.5,
                      }}
                    >
                      Save
                    </Button>
                  </Grid>
                </>
              ) : (

                <>
                  <Grid item>
                    <Box
                      component="img"
                      src={localPreview}
                      alt="New preview"
                      sx={{
                        width: 70,
                        height: 70,
                        objectFit: "contain",
                        borderRadius: 1,
                        border: "1px solid #c4c4c4",
                        padding: 0.2,
                      }}
                    />
                  </Grid>

                  <Grid item>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleCertSave}
                      disabled={!localPreview || uploadingCertificateBg}
                      startIcon={
                        uploadingCertificateBg && (
                          <ThreeDots
                            height="20"
                            width="20"
                            radius="9"
                            color="#fff"
                          />
                        )
                      }
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      {uploadingCertificateBg ? "Saving" : "Save"}
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={handleCertCancel}
                      disabled={uploadingCertificateBg}
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
